import { Colors } from 'services/ColorService';

/**
 * Semantic theme-token palette for the StudentDetail subtree.
 *
 * Direct analogue of `GroupDetail/parts/groupColors.ts`: every color used across
 * the Students list (`/students`) and the Student card (`/students/:id/details`)
 * is expressed here as a semantic name mapped to a `services/ColorService`
 * `Colors` token, so no component in the subtree needs a hardcoded hex literal
 * (Requirement 17.1) and the same mapping resolves under both the DEFAULT and
 * HACKERU themes (Requirement 17.4).
 *
 * `Colors` is the codebase's single source of truth for themed colors (a static
 * enum consumed throughout the app's styled-components). Referencing `Colors`
 * tokens IS the established theming mechanism, so consuming these semantic names
 * instead of hex literals makes the Student subtree theme-resolved by
 * construction. The existing semantic meanings are preserved (Requirement 17.3):
 * success = green, warn = amber, danger = red, neutral = slate, accent = blue.
 *
 * Where the legacy Student code used a hex with no exact `Colors` twin (the
 * slate text/background ramp), the nearest existing token is reused rather than
 * introducing a new hex literal.
 */
export const StudentColors = {
  // --- Status / money semantics (preserve existing meanings) ---
  /** Success / positive money (paid). Legacy `#16a34a`. */
  success: Colors.SuccessDefault,
  /** Warning / attention (debt, outstanding). Legacy `#d97706`. */
  warn: Colors.WarningDefault,
  /** Danger / error (overdue, lost). Legacy `#dc2626`. */
  danger: Colors.ErrorDefault,
  /** Success as TEXT. `success` — цвет точки и рамки, на белом он 2.36:1. */
  successText: 'var(--k-fg-256e29)',
  /** Warn as TEXT. Сумма долга красилась `warn` — 1.76:1 на главной цифре. */
  warnText: 'var(--k-fg-8a6100)',
  /** Danger as TEXT. `danger` как подпись даёт 3.96:1. */
  dangerText: 'var(--k-fg-c62828)',
  /** Neutral / muted slate (counts, placeholders, "no debt"). Legacy `#64748b`/`#94a3b8`. */
  neutral: Colors.Gray,
  /** Primary accent (links, active controls, focus). Legacy `#2563eb`. */
  accent: 'var(--k-fg-0d53fc)',
  /** Stronger accent for active/pressed text. Legacy `#1d4ed8` (nearest: Blue). */
  accentStrong: 'var(--k-fg-0d53fc)',

  // --- Text ramp ---
  /** Primary/heading text. Legacy `#0f172a` (nearest: Black). */
  textPrimary: 'var(--k-fg-263446)',
  /** Secondary body text. Legacy `#475569` (nearest existing token: Black). */
  textSecondary: 'var(--k-fg-263446)',
  /** Muted/auxiliary text — same token as `neutral`. Legacy `#94a3b8`. */
  textMuted: 'var(--k-fg-616e78)',

  // --- Surfaces / borders ---
  /** Default surface/background. Legacy `#fff`. */
  surface: 'var(--k-bg-ffffff)',
  /** Faint surface (zebra rows, lightest hover). Legacy `#f8fafc`. */
  surfaceSubtle: 'var(--k-bg-fafbfd)',
  /** Muted surface (hover, inset panels). Legacy `#f1f5f9`. */
  surfaceMuted: 'var(--k-bg-f2f5f9)',
  /** Tinted accent surface (active chip, accent hover). Legacy `#eff6ff`. */
  accentSurface: 'var(--k-bg-e7eeff)',
  /** Hairline borders/dividers. Legacy `#e2e8f0` (nearest: SecondLightGray). */
  border: 'var(--k-border-dfe6f1)',
} as const;

/** Semantic palette key names available to the StudentDetail subtree. */
export type StudentColorKey = keyof typeof StudentColors;

/**
 * Resolve a semantic Student color name to its themed `Colors` value.
 * Convenience getter for call sites that prefer a function over property access
 * (e.g. computing a debt accent conditionally).
 */
export const getStudentColor = (key: StudentColorKey): string =>
  StudentColors[key];
